// =============================================================================
// operational-view-client.http.ts — Adaptateur HTTP RÉEL du OperationalViewClient (S14, D2).
// =============================================================================
// Câble le read-model A-15 derrière le MÊME DI seam (OperationalViewClientProvider) : l'écran
// consomme l'interface OperationalViewClient, l'impl est substituée sans toucher l'UI.
//
// AUTORITÉ = API (NestJS, AdminSessionGuard, withAdmin policy s5, calcul AU VOL G0-S14-7a). Cet
// adaptateur ne porte AUCUNE règle métier : il sérialise les filtres (F), parse la réponse, mappe
// l'erreur. Les 3 routes sont des GET (admin LIT, jamais d'écriture — invariant n°1).
//
// SÉCURITÉ : la session admin est portée par le COOKIE httpOnly posé par l'API (credentials:
// 'include'). L'UI ne lit ni n'écrit le secret de session. Aucune donnée métier en localStorage
// (la persistance localStorage de l'écran A-15 ne porte QUE des préférences d'affichage, G0-S14-5).
// Les routes étant en GET, AUCUN en-tête CSRF n'est requis (le double-submit admin_csrf ne couvre
// que les méthodes MUTANTES — parité campaign-client.http.ts).

import {
  OperationalViewApiError,
  type CampaignHealthDto,
  type CampaignsDashboardResponseDto,
  type OperationalCardsDto,
  type OperationalViewClient,
  type OperationalViewFilters,
  type ProfileDetailResponseDto,
  type ProfileMatrixResponseDto,
  type SupplierDetailResponseDto,
  type SupplierListResponseDto,
} from '@/api/operational-view-client';

/** Forme attendue d'un corps d'erreur structuré (NestJS HttpException). */
interface StructuredErrorBody {
  readonly statusCode?: number;
  readonly message?: string;
}

/**
 * Adaptateur HTTP réel : `fetch` contre l'API admin operational-view. `baseUrl` par défaut vide
 * (chemins relatifs : SPA et API partagent l'origine, ou un reverse proxy route `/admin/*`). En
 * test, on injecte un baseUrl ou on mocke `fetch`.
 */
export class HttpOperationalViewClient implements OperationalViewClient {
  public constructor(private readonly baseUrl: string = '/api') {}

  /** Sérialise les filtres serveur (F) en query string (omet les valeurs absentes). */
  private query(filters: OperationalViewFilters): string {
    const params = new URLSearchParams();
    if (filters.cycleNumber !== undefined) {
      params.set('cycleNumber', String(filters.cycleNumber));
    }
    if (filters.profileCode !== undefined && filters.profileCode !== '') {
      params.set('profileCode', filters.profileCode);
    }
    if (filters.seniorityLevel !== undefined) {
      params.set('seniorityLevel', filters.seniorityLevel);
    }
    if (filters.supplierSiret !== undefined && filters.supplierSiret !== '') {
      params.set('supplierSiret', filters.supplierSiret);
    }
    const qs = params.toString();
    return qs === '' ? '' : `?${qs}`;
  }

  /** Exécute un GET admin (cookie de session) et renvoie le JSON typé, ou lève une erreur mappée. */
  private async get<T>(path: string): Promise<T> {
    const response = await fetch(`${this.baseUrl}${path}`, {
      method: 'GET',
      // Session admin = cookie httpOnly : transmis avec credentials: 'include'.
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
    });
    if (!response.ok) {
      throw await this.toApiError(response);
    }
    return (await response.json()) as T;
  }

  /** Convertit une réponse non-OK en erreur applicative typée (message serveur tel quel). */
  private async toApiError(response: Response): Promise<OperationalViewApiError> {
    let body: StructuredErrorBody | undefined;
    try {
      body = (await response.json()) as StructuredErrorBody;
    } catch {
      body = undefined;
    }
    const message =
      body?.message ??
      `Erreur API (${String(response.status)}) sur la vue opérationnelle.`;
    return new OperationalViewApiError(message, response.status);
  }

  public getCampaignsDashboard(): Promise<CampaignsDashboardResponseDto> {
    // V-DASH : base `admin/dashboard` (et NON `admin/campaigns/dashboard`, capturé par campaign `:id`).
    return this.get<CampaignsDashboardResponseDto>('/admin/dashboard/campaigns');
  }

  public getHealth(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<CampaignHealthDto> {
    return this.get<CampaignHealthDto>(
      `/admin/operational-view/${encodeURIComponent(campaignId)}/health${this.query(filters)}`,
    );
  }

  public getCards(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<OperationalCardsDto> {
    return this.get<OperationalCardsDto>(
      `/admin/operational-view/${encodeURIComponent(campaignId)}/cards${this.query(filters)}`,
    );
  }

  public getProfileMatrix(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<ProfileMatrixResponseDto> {
    return this.get<ProfileMatrixResponseDto>(
      `/admin/operational-view/${encodeURIComponent(campaignId)}/profile-matrix${this.query(filters)}`,
    );
  }

  public getSupplierList(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<SupplierListResponseDto> {
    return this.get<SupplierListResponseDto>(
      `/admin/operational-view/${encodeURIComponent(campaignId)}/supplier-list${this.query(filters)}`,
    );
  }

  public getProfileDetail(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<ProfileDetailResponseDto> {
    return this.get<ProfileDetailResponseDto>(
      `/admin/operational-view/${encodeURIComponent(campaignId)}/profile-detail${this.query(filters)}`,
    );
  }

  public getSupplierDetail(
    campaignId: string,
    filters: OperationalViewFilters,
  ): Promise<SupplierDetailResponseDto> {
    return this.get<SupplierDetailResponseDto>(
      `/admin/operational-view/${encodeURIComponent(campaignId)}/supplier-detail${this.query(filters)}`,
    );
  }
}
